import { getAllGraphStepChildrenIds } from './getAllGraphStepChildrenIds.js';
import { getParentGraphStep } from './getParentGraphStep.js';
import { randomId } from '../utils/randomId.js';

// Helper function to build a graph from steps (for copied steps)
const buildGraphFromSteps = (steps) => {
  const graph = {};
  steps.forEach((step, index) => {
    const nextStep = steps[index + 1];
    graph[step.id] = {
      exit: nextStep ? nextStep.id : null,
      child: null,
      branches: []
    };
  });
  return graph;
};

const findDisconnectedSubgraphs = (stepIds, graph) => {
  const hasIncomingConnection = new Set();
  const visited = new Set();
  const subgraphs = [];

  stepIds.forEach((stepId) => {
    const node = graph[stepId];
    if (node) {
      if (node.exit && stepIds.includes(node.exit)) {
        hasIncomingConnection.add(node.exit);
      }
      if (node.child && stepIds.includes(node.child)) {
        hasIncomingConnection.add(node.child);
      }
      if (node.branches) {
        node.branches.forEach((branchId) => {
          if (branchId && stepIds.includes(branchId)) {
            hasIncomingConnection.add(branchId);
          }
        });
      }
    }
  });

  const roots = stepIds.filter((stepId) => !hasIncomingConnection.has(stepId));

  roots.forEach((rootId) => {
    if (!visited.has(rootId)) {
      const subgraph = [];
      const traverse = (stepId) => {
        if (visited.has(stepId) || !stepIds.includes(stepId)) return;
        visited.add(stepId);
        subgraph.push(stepId);

        const node = graph[stepId];
        if (node) {
          if (node.exit) traverse(node.exit);
          if (node.child) traverse(node.child);
          if (node.branches) {
            node.branches.forEach((branchId) => {
              if (branchId) traverse(branchId);
            });
          }
        }
      };

      traverse(rootId);
      if (subgraph.length > 0) {
        subgraphs.push({ root: rootId, steps: subgraph });
      }
    }
  });

  return subgraphs;
};

const determineTargetRelation = (parentStepId, parentStep, targetSteps, copiedSteps, sourceGraph) => {
  // Find the parent step data to understand its capabilities
  const parentStepData = targetSteps.find((step) => step.id === parentStepId);

  if (!parentStepData) {
    return { relation: 'exit', index: null };
  }

  // Check if parent can have branches (like ifElse steps)
  const canHaveBranches = parentStepData.appSlug === 'ifElse';

  // Check if parent can have children (like iterate steps)
  const canHaveChildren = ['iterate'].includes(parentStepData.appSlug) || parentStepData.options?.pagination?.type === 'batch';

  // If we have copied steps, try to determine their original relationship
  if (copiedSteps && sourceGraph) {
    const firstCopiedStep = copiedSteps[0];
    if (firstCopiedStep) {
      const originalParent = getParentGraphStep(firstCopiedStep.id, sourceGraph);

      // If the original steps were in a branch relationship and the target can have branches
      if (originalParent?.relation === 'branch' && canHaveBranches) {
        // Find the first available branch slot
        const currentBranches = parentStep.branches || [];
        const firstEmptyBranchIndex = currentBranches.findIndex((branch) => !branch);
        if (firstEmptyBranchIndex !== -1) {
          return { relation: 'branch', index: firstEmptyBranchIndex };
        }
        // If no empty branch slots, add to the end
        return { relation: 'branch', index: currentBranches.length };
      }

      // If the original steps were children and the target can have children
      if (originalParent?.relation === 'child' && canHaveChildren && !parentStep.child) {
        return { relation: 'child', index: null };
      }
    }
  }

  // Default fallback logic based on parent capabilities
  if (canHaveBranches && (!parentStep.branches || parentStep.branches.filter((b) => b).length === 0)) {
    // If parent can have branches and has no branches yet, use first branch
    return { relation: 'branch', index: 0 };
  }

  if (canHaveChildren && !parentStep.child) {
    // If parent can have children and has no child yet, use child relationship
    return { relation: 'child', index: null };
  }

  // Default to exit relationship
  return { relation: 'exit', index: null };
};

export const duplicateWorkflowStepsByIds = ({ workflow, ids, userId, parentStepId, copiedSteps, copiedGraph }) => {
  const parentIncludedInCopy = parentStepId && ids.includes(parentStepId);

  const sourceSteps = copiedSteps || [...(workflow?.steps || [])];
  const sourceGraph = copiedGraph || (copiedSteps ? buildGraphFromSteps(copiedSteps) : { ...(workflow?.graph || {}) });
  const targetSteps = [...(workflow?.steps || [])];
  const targetGraph = { ...(workflow?.graph || {}) };

  // Check if there are any folders, and fill in the children
  ids.forEach((id) => {
    const step = sourceSteps.find((n) => n.id === id);
    if (step?.appSlug === 'folder') {
      const children = getAllGraphStepChildrenIds(sourceGraph, id);
      children.forEach((childId) => {
        if (!ids.includes(childId)) {
          ids.push(childId);
        }
      });
    }
  });

  let filteredIds = [...ids];
  if (parentIncludedInCopy) {
    filteredIds = ids.filter((id) => id !== parentStepId);
  }

  // Grab only the action ids
  const actionIds = filteredIds.filter((n) => {
    const step = sourceSteps.find((m) => m.id === n);
    return !['schedule'].includes(step?.appSlug) && step?.type !== 'trigger';
  });

  // Build a clone id map
  const idMap = {};
  filteredIds?.forEach((n) => {
    idMap[n] = randomId(12, 'all');
  });

  const newDate = new Date().toISOString();

  // Duplicate the steps
  const newSteps = [];
  for (let i = 0; i < filteredIds?.length; ++i) {
    const stepId = filteredIds[i];
    const step = { ...sourceSteps?.find((n) => n.id === stepId) };
    const newStepId = idMap[stepId];
    const newStep = {
      ...step,
      id: newStepId,
      // Tenant ownership of a duplicated step belongs to the TARGET workflow's company,
      // never the source row's. Without this, cross-workflow paste from a different
      // tenant inserts an orphan row invisible to selectWorkflow's company-scoped read.
      companyId: workflow?.companyId,
      workflowId: workflow?.id,
      createdOn: newDate,
      updatedOn: newDate,
      createdBy: userId,
      updatedBy: userId
    };
    newSteps.push(newStep);
  }

  // Create new graph with connections
  const newGraph = {};

  // Determine the connection strategy based on context
  if (copiedSteps || parentStepId) {
    // Copy-paste or specific parent connection scenario
    if (copiedGraph) {
      // Use the preserved graph structure when available
      for (let i = 0; i < actionIds.length; i++) {
        const currentId = actionIds[i];
        const newCurrentId = idMap[currentId];
        const originalNode = sourceGraph[currentId];

        if (originalNode) {
          const newNode = {
            exit: originalNode.exit && idMap[originalNode.exit] ? idMap[originalNode.exit] : null,
            child: originalNode.child && idMap[originalNode.child] ? idMap[originalNode.child] : null,
            branches: originalNode.branches ? originalNode.branches.map((branchId) => (branchId && idMap[branchId] ? idMap[branchId] : null)) : []
          };

          // If parent was included in copy and filtered out, update references to it
          if (parentIncludedInCopy) {
            if (originalNode.exit === parentStepId) {
              newNode.exit = null;
            }
            if (originalNode.child === parentStepId) {
              newNode.child = null;
            }
            if (originalNode.branches && originalNode.branches.includes(parentStepId)) {
              newNode.branches = newNode.branches.map((branchId) => (branchId === null && originalNode.branches.includes(parentStepId) ? null : branchId));
            }
          }

          newGraph[newCurrentId] = newNode;
        } else {
          newGraph[newCurrentId] = {
            exit: null,
            child: null,
            branches: []
          };
        }
      }
    } else {
      // Fallback to linear sequence for backwards compatibility
      for (let i = 0; i < actionIds.length; i++) {
        const currentId = actionIds[i];
        const newCurrentId = idMap[currentId];
        const nextId = actionIds[i + 1];
        const newNextId = nextId ? idMap[nextId] : null;

        newGraph[newCurrentId] = {
          exit: newNextId,
          child: null,
          branches: []
        };
      }
    }

    // Handle parent connection if specified
    if (parentStepId) {
      const parentStep = targetGraph[parentStepId];
      if (!parentStep) {
        return null;
      }

      // Find all disconnected subgraphs and connect them
      const subgraphs = findDisconnectedSubgraphs(actionIds, sourceGraph);

      if (subgraphs.length === 0) {
        return null;
      }

      // Connect subgraphs in sequence: parent → subgraph1 → subgraph2 → ... → original_parent_exit
      const firstSubgraph = subgraphs[0];
      const firstNewStepId = idMap[firstSubgraph.root];

      // Chain multiple subgraphs together if needed
      if (subgraphs.length > 1) {
        for (let i = 0; i < subgraphs.length - 1; i++) {
          const currentSubgraph = subgraphs[i];
          const nextSubgraph = subgraphs[i + 1];

          // Find the last step of current subgraph (step with no exit within subgraph)
          let lastStepInSubgraph = null;
          for (const stepId of currentSubgraph.steps) {
            const node = sourceGraph[stepId];
            if (!node?.exit || !currentSubgraph.steps.includes(node.exit)) {
              lastStepInSubgraph = stepId;
              break;
            }
          }

          if (lastStepInSubgraph && newGraph[idMap[lastStepInSubgraph]]) {
            // Connect last step of current subgraph to root of next subgraph
            newGraph[idMap[lastStepInSubgraph]] = {
              ...newGraph[idMap[lastStepInSubgraph]],
              exit: idMap[nextSubgraph.root]
            };
          }
        }
      }

      // Get the last step of the final subgraph for parent connection
      const lastSubgraph = subgraphs[subgraphs.length - 1];
      let lastNewStepId = null;
      for (const stepId of lastSubgraph.steps) {
        const node = sourceGraph[stepId];
        if (!node?.exit || !lastSubgraph.steps.includes(node.exit)) {
          lastNewStepId = idMap[stepId];
          break;
        }
      }

      if (!lastNewStepId) {
        lastNewStepId = idMap[lastSubgraph.root]; // fallback
      }

      // Determine the appropriate relationship type and index
      const { relation, index } = determineTargetRelation(parentStepId, parentStep, targetSteps, copiedSteps, sourceGraph);

      // Handle different relationship types
      if (relation === 'exit') {
        const originalParentExit = parentStep.exit;

        // Connect parent to first new step via exit
        targetGraph[parentStepId] = {
          ...parentStep,
          exit: firstNewStepId
        };

        // Connect last new step to original parent's exit (preserving the original flow)
        // This inserts the copied steps between the parent and its original next step
        if (newGraph[lastNewStepId]) {
          newGraph[lastNewStepId] = {
            ...newGraph[lastNewStepId],
            exit: originalParentExit
          };
        }
      } else if (relation === 'child') {
        const originalParentChild = parentStep.child;

        // Connect parent to first new step via child
        targetGraph[parentStepId] = {
          ...parentStep,
          child: firstNewStepId
        };

        // Connect last new step to original parent's child (preserving the original flow)
        // This inserts the copied steps between the parent and its original child
        if (newGraph[lastNewStepId]) {
          newGraph[lastNewStepId] = {
            ...newGraph[lastNewStepId],
            exit: originalParentChild
          };
        }
      } else if (relation === 'branch' && index !== null) {
        const originalParentBranches = [...(parentStep.branches || [])];
        const originalBranchConnection = originalParentBranches[index];

        // Ensure branches array is large enough
        while (originalParentBranches.length <= index) {
          originalParentBranches.push(null);
        }

        // Connect parent to first new step via branch
        originalParentBranches[index] = firstNewStepId;
        targetGraph[parentStepId] = {
          ...parentStep,
          branches: originalParentBranches
        };

        // For branches, only connect to original branch connection if there was one
        // This allows replacing existing branch content or adding to empty branch slots
        if (newGraph[lastNewStepId] && originalBranchConnection) {
          newGraph[lastNewStepId] = {
            ...newGraph[lastNewStepId],
            exit: originalBranchConnection
          };
        }
      }
    }
  } else {
    // Original Ctrl+D workflow duplication
    for (let i = 0; i < actionIds.length; i++) {
      const currentId = actionIds[i];
      const newCurrentId = idMap[currentId];
      const originalNode = sourceGraph[currentId];

      // Preserve original graph structure with new IDs
      newGraph[newCurrentId] = {
        exit: originalNode?.exit ? idMap[originalNode.exit] : null,
        child: originalNode?.child ? idMap[originalNode.child] : null,
        branches: originalNode?.branches?.map((b) => idMap[b] || null).filter(Boolean) || []
      };
    }

    if (actionIds.length > 0) {
      // Handle last step connection to first step of original duplicate
      const firstOriginalStepId = actionIds[0];
      const lastOriginalStepId = actionIds[actionIds.length - 1];
      const lastOriginalNode = sourceGraph[lastOriginalStepId];
      const originalLastExit = lastOriginalNode?.exit;

      targetGraph[lastOriginalStepId].exit = idMap[firstOriginalStepId];

      // If there was an exit from the last step, connect to the first step of the new duplicate
      if (originalLastExit) {
        newGraph[idMap[lastOriginalStepId]] = {
          ...newGraph[idMap[lastOriginalStepId]],
          exit: originalLastExit
        };
      }
    }
  }

  // Merge graphs and steps
  const fullGraph = { ...targetGraph, ...newGraph };
  const fullSteps = [...targetSteps, ...newSteps];

  return { graph: fullGraph, steps: fullSteps, newSteps, timestamp: newDate };
};
